import { Link } from 'react-router-dom';
import { articles } from '../../../mocks/articles';

const GUIDE_LIMIT = 6;

const getCategoryColorClasses = (color: string) => {
  const colors: Record<string, { bg: string; text: string; border: string; dot: string }> = {
    purple: { bg: 'bg-purple-600/20', text: 'text-purple-400', border: 'border-purple-500/30', dot: 'bg-purple-400' },
    blue:   { bg: 'bg-cyan-600/20',   text: 'text-cyan-400',   border: 'border-cyan-500/30',   dot: 'bg-cyan-400'   },
    green:  { bg: 'bg-emerald-600/20',text: 'text-emerald-400',border: 'border-emerald-500/30',dot: 'bg-emerald-400'},
  };
  return colors[color] ?? colors.purple;
};

export default function LatestBuyingGuides() {
  const guides = [...articles]
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .slice(0, GUIDE_LIMIT);

  return (
    <section className="py-14 relative overflow-hidden">
      {/* Ambient glows */}
      <div className="absolute inset-0 bg-purple-900/5 pointer-events-none" />
      <div className="absolute top-10 left-[8%] w-72 h-72 bg-purple-600/7 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-10 right-[6%] w-60 h-60 bg-emerald-500/6 rounded-full blur-3xl pointer-events-none" />

      {/* Sparse floating dots */}
      {[...Array(8)].map((_, i) => (
        <div
          key={i}
          className={`absolute rounded-full animate-pulse pointer-events-none ${i % 2 === 0 ? 'w-1 h-1 bg-purple-400/35' : 'w-0.5 h-0.5 bg-cyan-400/30'}`}
          style={{
            left: `${(i * 13 + 4) % 95}%`,
            top: `${(i * 17 + 7) % 90}%`,
            animationDelay: `${(i * 0.4) % 3}s`,
            animationDuration: `${2.5 + (i * 0.2) % 2}s`,
          }}
        />
      ))}

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Section header */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-white/20 rounded-full text-xs uppercase tracking-widest text-white/80 backdrop-blur-sm bg-white/5 mb-3">
              <span className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse inline-block" />
              Buying Guides
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white">
              Latest Buying Guides
              <div className="h-0.5 w-24 bg-gradient-to-r from-purple-500 via-cyan-500 to-purple-500 mt-3 rounded-full shadow-lg shadow-purple-500/50" />
            </h2>
            <p className="text-gray-400 mt-2 text-sm">
              Research-backed guides updated for 2026. Find exactly what to buy.
            </p>
          </div>
          <Link
            to="/articles"
            className="inline-flex items-center gap-2 px-5 py-2.5 border border-white/20 text-white/80 rounded-lg text-sm font-medium hover:border-cyan-400/50 hover:text-cyan-400 transition-all duration-300 whitespace-nowrap cursor-pointer self-start sm:self-auto"
          >
            View All
            <i className="ri-arrow-right-line" />
          </Link>
        </div>

        {/* Article grid — fully clickable cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-7">
          {guides.map((article) => {
            const cc = getCategoryColorClasses(article.categoryColor);
            return (
              <Link
                key={article.id}
                to={`/article/${article.slug}`}
                className="group flex flex-col bg-gray-900/30 backdrop-blur-sm rounded-2xl overflow-hidden border border-white/10 hover:border-purple-400/30 transition-all duration-500 hover:shadow-2xl hover:shadow-purple-500/10 cursor-pointer"
              >
                {/* Thumbnail */}
                <div className="relative h-52 flex-shrink-0 overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-gray-950/30 to-transparent" />

                  {/* Category badge */}
                  <div className="absolute top-4 left-4">
                    <span className={`inline-block px-3 py-1 ${cc.bg} ${cc.text} rounded-full text-xs font-semibold uppercase tracking-wider border ${cc.border} backdrop-blur-sm`}>
                      {article.category}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="flex flex-col flex-1 p-6 space-y-3">
                  {/* Meta */}
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <i className="ri-calendar-line" />
                      {article.date}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-time-line" />
                      {article.readTime}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line" />
                      {article.views}
                    </span>
                  </div>

                  <h3 className="text-lg font-bold text-white group-hover:text-purple-300 transition-colors duration-300 leading-snug line-clamp-2">
                    {article.title}
                  </h3>

                  <p className="text-sm text-gray-400 leading-relaxed flex-1 line-clamp-2">
                    {article.excerpt}
                  </p>

                  {/* CTA row */}
                  <div className="pt-3 flex items-center justify-between">
                    <span className="text-sm font-semibold text-purple-400 group-hover:text-cyan-400 transition-colors duration-300 flex items-center gap-1.5">
                      Read Guide
                      <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1" />
                    </span>
                    <span className={`w-2 h-2 rounded-full ${cc.dot} opacity-60`} />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-12">
          <Link
            to="/articles"
            className="inline-flex items-center gap-2 px-8 py-4 border border-white/20 text-white rounded-lg font-semibold hover:bg-white/5 hover:border-cyan-400/40 transition-all duration-300 whitespace-nowrap cursor-pointer text-sm"
          >
            <i className="ri-list-check-2" />
            Browse All Buying Guides
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </div>
    </section>
  );
}
